import { FieldNames, FormType } from "@/enums";
import {
  IDictionary,
  IProjectRole,
  ISelectOption,
  ITranslation,
} from "@/interfaces";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { Form, Modal, Select } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IOptions {
  [id: string]: ITranslation;
}

interface IProps {
  open: boolean;
  projectRole: IProjectRole;
  selectedRegion: IDictionary;
  formFieldOptions: IOptions;
  onClose(): void;
  onOk(functionalArea: string): void;
}

export const AddRegionalRoleModal = ({
  open,
  projectRole,
  selectedRegion,
  formFieldOptions,
  onClose,
  onOk,
}: IProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [functionalAreas, setFunctionalAreas] = useState<ISelectOption[]>([]);

  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();

  useEffect(() => {
    if (!formFieldOptions) return;

    const options: ISelectOption[] = Object.keys(formFieldOptions).map(
      (key) => ({
        value: key,
        label: getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.FunctionalArea,
          key
        ),
      })
    );

    setFunctionalAreas(options);
  }, [formFieldOptions, getPropertyValueDisplay]);

  const handleAdd = (): void => {
    form.validateFields().then((data: { functionalArea: string }) => {
      onOk(data.functionalArea);
      form.resetFields();
    });
  };

  const handleCancel = (): void => {
    form.resetFields();
    onClose();
  };

  const isDuplicateRegionRole = (value: string): boolean | undefined =>
    projectRole?.regionalProjectRoles?.some(
      (regionRole) =>
        regionRole.region === selectedRegion.key &&
        regionRole.functionalArea === value
    );

  return (
    <Modal
      centered
      closable={false}
      title={intl.formatMessage(
        { id: "projectRoles.addRegionalRoleDynamic" },
        { region: selectedRegion?.value }
      )}
      open={open}
      cancelText={intl.formatMessage({ id: "general.cancel" })}
      okText={intl.formatMessage({ id: "general.add" })}
      onCancel={handleCancel}
      onOk={handleAdd}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="functionalArea"
          label={intl.formatMessage({ id: "projectRoles.functionalArea" })}
          rules={[
            { required: true, message: "" },
            {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              validator(_, value) {
                if (value && isDuplicateRegionRole(value)) {
                  return Promise.reject(
                    new Error(
                      intl.formatMessage({
                        id: "projectRoles.duplicateRegionRoleError",
                      })
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select options={functionalAreas} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
